import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useGroupMemberActions, useGroupMemberState } from '../../shared/hooks/useApi/useGroupMember';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { useParams } from 'react-router-dom';
import { isPageOwnerMatchedToRaw } from '../../shared/util/pageOwnerMatch';
import GroupMember from './GroupMember';
import GroupMemberInfo from './GroupMemberInfo';
import GroupMemberViewerShell from './GroupMemberViewerShell';
import EmptyGroupMemberList from './EmptyGroupMemberList';
import HintWrapper from '../../shared/component/headless/button/HintWrapper';
import { getHintMessage } from '../../shared/constants/hintMessages';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import createIcon from '../../asset/icon/create.png';
import reorderIcon from '../../asset/icon/reorder.png';
import confirmIcon from '../../asset/icon/confirm2.png';
import confirmIcon2 from '../../asset/icon/confirm3.png';
import { useGroupMemberTagStore } from '../../shared/store/groupMemberTagStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { AlertPosition, AlertType, DeviceType, ModalType } from '../../shared/enum/EnumRepository';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { useIsPageOwner } from '../../shared/hooks/useIsPageOwner';
import { useValidation } from '../../shared/hooks/useValidation';
import { isValidHttpUrl } from '../../shared/util/urlValidation';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import {
  canShowGroupMemberList,
  canShowGroupMemberTagList,
} from '../../shared/util/visibility/groupMemberVisibility';
import type { ResourceScope } from '../../shared/util/scopedResource';
import { useApprovalRequestActions } from '../../shared/hooks/useApi/useApprovalRequest';
import MobileContextToolbar from '../../shared/component/MobileContextToolbar';
import { useAuthStore, useAuthTokenStore } from '../../shared/store/authStore';

const GroupMemberList: React.FC = () => {
  const isOwner = useIsPageOwner();
  const user = useAuthStore((state) => state.user);
  const authHydrated = useAuthStore((state) => state.hasHydrated);
  const hasAuthToken = useAuthTokenStore((state) => Boolean(state.authToken));
  const isAuthReady = authHydrated && (!user || hasAuthToken);
  const { isEditMode } = useEditMode();
  const {
    groupMemberList,
    listOwnerAui,
    listScope,
    status,
    hasChanged,
  } = useGroupMemberState();
  const {
    getGroupMemberList,
    getGroupMemberListOwner,
    createGroupMember,
    updateChangedGroupMemberBulk,
  } = useGroupMemberActions();
  const { getApprovalRequestList } = useApprovalRequestActions();
  const { isEmail } = useValidation();
  const { enqueueAlert } = useStandardAlertStore();
  const selectedTags = useGroupMemberTagStore((s) => s.selectedTags);
  const tagListOwnerAui = useGroupMemberTagStore((s) => s.listOwnerAui);
  const tagStatus = useGroupMemberTagStore((s) => s.status);
  const setStandardModal = useModalStore((s) => s.setStandardModal);
  const isMobile = useDeviceStore((s) => s.deviceType === DeviceType.MOBILE);
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const { HANDLE: raw } = useParams<{ HANDLE?: string }>();
  const groupMemberListRef = useRef<HTMLDivElement>(null);
  const currentScope: ResourceScope = isOwner ? 'owner' : 'public';
  const canShowList = canShowGroupMemberList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    listScope,
    currentScope,
    status,
  });
  const canShowTags = canShowGroupMemberTagList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui: tagListOwnerAui,
    status: tagStatus,
  });
  const visibleGroupMemberList = canShowList ? groupMemberList : [];
  const activeSelectedTags = canShowTags ? selectedTags : [];

  const filteredGroupMemberList = useMemo(() => {
    if (activeSelectedTags.length === 0) return visibleGroupMemberList;
    const selectedTagIds = new Set(activeSelectedTags.map((tag) => tag.id));
    return visibleGroupMemberList.filter((member) =>
      member.tags.some((tag) => selectedTagIds.has(tag.id)),
    );
  }, [visibleGroupMemberList, activeSelectedTags]);

  useEffect(() => {
    groupMemberListRef.current?.focus();
  }, []);

  useEffect(() => {
    const getGroupMemberListWithApi = async () => {
      if (!isAuthReady) return;
      if (!isPageOwnerMatchedToRaw(raw, pageOwner)) return;
      if (isOwner) {
        await Promise.all([
          getGroupMemberListOwner(),
          getApprovalRequestList(),
        ]);
        return;
      }
      await getGroupMemberList();
    };
    getGroupMemberListWithApi();
  }, [raw, pageOwner?.aui, isOwner, isAuthReady]);

  const handleCreateGroupMember = async () => {
    await createGroupMember();
  };

  const handleUpdateChanged = async () => {
    const invalidEmailMember = groupMemberList.find(
      (member) => member.hasChanged && member.email && !isEmail(member.email),
    );
    if (invalidEmailMember) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_EMAIL_FORMAT'),
      });
      document.getElementById(`group-member-${invalidEmailMember.id}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      return;
    }
    const invalidUrlMember = groupMemberList.find(
      (member) => member.hasChanged && member.sns.url1 && !isValidHttpUrl(member.sns.url1),
    );
    if (invalidUrlMember) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_URL_FORMAT'),
      });
      document.getElementById(`group-member-${invalidUrlMember.id}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      return;
    }
    await updateChangedGroupMemberBulk();
  };

  const handleReorderGroupMembers = () => {
    setStandardModal({
      modalType: ModalType.INDEXING,
      title: 'Group Member',
      value: null,
      handleChange: () => {},
    });
  };

  const renderConfirmButton = (transparent = false, keepMounted = false) => {
    if (!hasChanged && !keepMounted) return null;
    return (
      <HintWrapper content={getHintMessage('UPDATE_GROUP_MEMBER')} toLeft={!isMobile}>
        <UniversalIconBtn
          isPrimary
          size={transparent ? "fit" : "standard"}
          border={!transparent}
          isTransparent={transparent}
          backgroundImg={confirmIcon}
          backgroundImgHover={confirmIcon2}
          onClick={handleUpdateChanged}
        />
      </HintWrapper>
    );
  };

  const renderCreateButton = (transparent = false) => (
    <HintWrapper content={getHintMessage('CREATE_GROUP_MEMBER')} toLeft={!isMobile}>
      <UniversalIconBtn
        size={transparent ? "fit" : "standard"}
        border={!transparent}
        isTransparent={transparent}
        backgroundImg={createIcon}
        onClick={handleCreateGroupMember}
      />
    </HintWrapper>
  );

  const renderReorderButton = (transparent = false) => (
    groupMemberList.length > 1 ? (
      <HintWrapper content={getHintMessage('REORDER_GROUP_MEMBERS')} toLeft={!isMobile}>
        <UniversalIconBtn
          size={transparent ? "fit" : "standard"}
          border={!transparent}
          isTransparent={transparent}
          backgroundImg={reorderIcon}
          onClick={handleReorderGroupMembers}
        />
      </HintWrapper>
    ) : null
  );

  return (
    <GroupMemberListContainer
      tabIndex={0}
      ref={groupMemberListRef}
      $isMobile={isMobile}
    >
      {!isMobile && (
        <GroupMemberListComp>
          {filteredGroupMemberList.map((each) =>
            <GroupMemberInfo
              key={each.id}
              data={each}
            />
          )}
          {canShowList && filteredGroupMemberList.length === 0 && <EmptyGroupMemberList />}
        </GroupMemberListComp>
      )}
      <GroupMemberViewerShell isMobile={isMobile}>
        {canShowList && (
          filteredGroupMemberList.length === 0
            ? <EmptyGroupMemberPanel>No members</EmptyGroupMemberPanel>
            : (
              <GroupMemberCardList>
                {filteredGroupMemberList.map((member) => (
                  <GroupMember
                    key={member.id}
                    member={member}
                  />
                ))}
              </GroupMemberCardList>
            )
        )}
      </GroupMemberViewerShell>
      {isEditMode && isOwner && (
        isMobile ? (
          <MobileContextToolbar
            hasChanged={hasChanged}
            confirmAction={renderConfirmButton(true, true)}
          >
            {renderCreateButton(true)}
            {renderReorderButton(true)}
          </MobileContextToolbar>
        ) : (
          <BtnContainer data-has-changed={hasChanged ? 'true' : 'false'}>
            {renderConfirmButton()}
            {renderCreateButton()}
            {renderReorderButton()}
          </BtnContainer>
        )
      )}
    </GroupMemberListContainer>
  );
}

export default GroupMemberList;

const GroupMemberListContainer = styled.article<{ $isMobile: boolean }>`
  width: 100%;
  height: calc(86vh);
  display: flex;
  outline: none;
  position: relative;

  ${({ $isMobile }) =>
    $isMobile &&
    `
      flex: 1;
      min-height: 0;
      height: auto;
      flex-direction: column;
      overflow: hidden;
    `}
`;

const GroupMemberCardList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmptyGroupMemberPanel = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const GroupMemberListComp = styled.section`
  width: 14vw;
  max-width: 300px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow-y: auto;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const BtnContainer = styled.div`
  position: fixed;
  z-index: 4;
  display: flex;
  align-items: center;
  bottom: clamp(25px, 5vh, 50px);
  right: clamp(25px, 5vw, 75px);
  width: fit-content;
  justify-content: flex-end;
  gap: 4px;
`;
